import type {
    DateFilterModel,
    FilterModel,
    NumberFilterModel,
    TextFilterModel,
} from '@ag-grid-community/core';
import type { ISimpleFilterModelType } from '@ag-grid-community/core/dist/esm/es6/filter/provided/simpleFilter';

type Filter = Record<string, FilterModel>;

/**
 * Transform search parameters into AG-Grid filter models
 * @param columnIds - The query parameters keys that are interesting, a whitelist of sort.
 * @param searchParams - The search parameters to extract the values from
 * @returns A record of columnIds and respective AG-Grid filter models
 * @remarks
 * ```ts
 * // ?title=equals:foo&amount=greaterThan:100
 * // turns into
 * {
 *   title: { filterType: 'text', type: 'equals', filter: 'foo' },
 *   amount: { filterType: 'number', type: 'greaterThan', filter: 100 }
 * }
 * ```
 */
export function createFilterFromQueryParams(
    columnIds: string[],
    searchParams: URLSearchParams,
): Filter {
    const initialState: Filter = {};
    // Loop through all the keys and extract the values from the searchParams
    return columnIds.reduce((acc, colId) => {
        const hasKey = searchParams.has(colId);
        if (hasKey) {
            const paramValue = searchParams.get(colId);
            // Exit early if the value is falsy
            if (!paramValue) return acc;
            // Extract the value and operator from the query parameter
            // if no operator is there, it falls back to "equals"
            const { value, operator } = parseQueryParamValue(
                paramValue,
                'equals',
            );
            // Determine the type of the value
            const type = getType(value);
            // Create the filter model
            const model = createFilterModel(type, value, operator);
            // Add the filter model to the record
            acc[colId] = model;
        }
        return acc;
    }, initialState);
}

/**
 * Parse the query parameter value into an operator and a value
 * @param queryParamValue - The query parameter value to parse
 * @param fallbackOperator - The operator to use if none is provided
 * @remarks
 * ?title=equals:foo turns into { operator: 'equals', value: 'foo' }
 * ?title=foo turns into { operator: 'contains', value: 'foo' }
 */
export function parseQueryParamValue(
    queryParamValue: string,
    fallbackOperator = 'contains',
): { operator: string; value: string } {
    if (queryParamValue.includes(':')) {
        const [operator, value] = queryParamValue
            .split(':')
            .map(decodeURIComponent);
        return { operator, value };
    }

    return {
        operator: fallbackOperator,
        value: decodeURIComponent(queryParamValue),
    };
}

/**
 * Determine the type of the value
 * @param value - The value to determine the type of
 * @returns "number" if the value is a number, "date" if the value is a date, "text" otherwise
 */
function getType(value: string): 'number' | 'date' | 'text' {
    // Check if the value is a number
    if (!isNaN(Number(value))) {
        return 'number';
    }

    // Check if the value is a valid date
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
        return 'date';
    }

    // If it's not a number or a date, it's a string
    return 'text';
}

function createFilterModel(
    type: 'text' | 'number' | 'date',
    value: string,
    operator: string,
): FilterModel {
    switch (type) {
        case 'text':
            return createTextFilterModel(value, operator);
        case 'number':
            return createNumberFilterModel(value, operator);
        case 'date':
            return createDateFilterModel({ dateFrom: value, operator });
        default:
            throw new Error(`Filter type ${type} not supported`);
    }
}

function createTextFilterModel(
    value: string,
    operator: string,
): TextFilterModel {
    return {
        filterType: 'text',
        type: operator as ISimpleFilterModelType,
        filter: value,
    };
}

function createNumberFilterModel(
    value: string,
    operator: string,
): NumberFilterModel {
    return {
        filterType: 'number',
        type: operator as ISimpleFilterModelType,
        filter: Number(value),
    };
}

interface CreateDateFilterModelOptions {
    dateFrom: string;
    dateTo?: string;
    operator?: string;
}

function createDateFilterModel({
    dateFrom,
    dateTo,
    operator = 'greaterThanOrEqual',
}: CreateDateFilterModelOptions): DateFilterModel {
    return {
        filterType: 'date',
        dateFrom,
        dateTo: dateTo ? dateTo : null,
        type: operator as ISimpleFilterModelType,
    };
}
